/* ============================================================================
   TurnOrder — JURNALUL MESAJELOR (toast log)
   ----------------------------------------------------------------------------
   RO: Mesajele care apar în colț la fiecare acțiune („Completează data și jocul!",
       „Clubul a atins numărul maxim de membri…") dispar după ~3 secunde. Cine
       citește mai încet, cine e distras, sau cine primește patru mesaje deodată
       nu apucă să le citească — și informația se pierde definitiv.
       Fișierul ăsta le păstrează, pe durata sesiunii, într-un jurnal pe care
       utilizatorul îl poate deschide din panoul de notificări.

       ⚠ NU sunt notificările din baza de date (invitații, mesaje, sesiuni).
       Acelea sunt evenimente între oameni și trăiesc pe server. Astea sunt
       răspunsuri ale aplicației la propriile tale acțiuni: utilitare, personale,
       fără valoare după ce închizi sesiunea. De aceea stau în `sessionStorage`
       și se șterg la deconectare — nimic nu ajunge în baza de date.

   EN: Keeps the ephemeral toast messages in a per-session log the user can read
       later. Not the DB notifications; nothing is persisted server-side.

   ARHITECTURĂ: script clasic, fără module. Se încarcă DUPĂ `app-modules-2.js`
   (unde e definit `showNotification`) și îl împachetează — nu îl rescrie, ca
   să nu existe două versiuni ale aceleiași funcții (§5 Override Map).
   ============================================================================ */
'use strict';

/* Câte mesaje păstrăm. Peste atât, cele mai vechi se pierd — un jurnal de sesiune
   nu are rost să crească la nesfârșit. */
var TOASTLOG_MAX = 60;
var TOASTLOG_KEY = 'to_toastlog';

var _toastLog = [];

/* ── Persistență pe durata sesiunii ──────────────────────────────────────────
   `sessionStorage`, nu `localStorage`: jurnalul supraviețuiește unei reîncărcări
   de pagină (inclusiv celei de după activarea profilului), dar dispare când se
   închide fila. Zero urme pe termen lung. */
function toastLogLoad() {
    try {
        var raw = sessionStorage.getItem(TOASTLOG_KEY);
        _toastLog = raw ? (JSON.parse(raw) || []) : [];
        if (!Array.isArray(_toastLog)) _toastLog = [];
    } catch (e) { _toastLog = []; }
}

function toastLogSave() {
    try { sessionStorage.setItem(TOASTLOG_KEY, JSON.stringify(_toastLog)); }
    catch (e) { /* mod privat strict, sau cotă plină: jurnalul rămâne doar în memorie */ }
}

function toastLogClear(inchidePanoul) {
    _toastLog = [];
    try { sessionStorage.removeItem(TOASTLOG_KEY); } catch (e) { /* ignorăm */ }
    var panel = null;
    try { panel = document.getElementById('toastLogPanel'); } catch (e) { /* ignorăm */ }
    /* ⚠ La deconectare panoul TREBUIE închis, nu doar golit: altfel rămâne
       deschis peste ecranul de întâmpinare, arătând conținut care aparține
       contului tocmai deconectat. */
    if (inchidePanoul && panel) { try { panel.classList.add('hidden'); } catch (e) { /* ignorăm */ } }
    try { if (typeof renderToastLogPanel === 'function') renderToastLogPanel(); } catch (e) { /* ignorăm */ }
}
window.toastLogClear = toastLogClear;

/* ── Ce merită păstrat ───────────────────────────────────────────────────────
   NU tot. Mesajele de progres („Se încarcă…", „Adding player…") nu au ce căuta
   într-un jurnal: sunt un semn că aplicația lucrează, nu o informație de citit.
   Le recunoaștem după punctele de suspensie de la final — convenția folosită
   consecvent în aplicație. Restul se păstrează, indiferent de tip: și un mesaj
   de succes poate conține ceva ce vrei să recitești (numele clubului în care
   tocmai ai intrat, de exemplu). */
function toastLogShouldKeep(mesaj, tip) {
    var m = String(mesaj || '').trim();
    if (!m) return false;
    if (/(\.\.\.|…)$/.test(m)) return false;          /* mesaj de progres */
    return true;
}

/* ── Adăugarea ───────────────────────────────────────────────────────────────
   Mesajele identice consecutive NU se dublează: se incrementează un contor.
   Fără asta, o validare care se declanșează de patru ori (vezi cazul real cu
   „Completează data și jocul!") ar umple jurnalul cu același rând. */
function toastLogAdd(mesaj, tip) {
    try {
        if (!toastLogShouldKeep(mesaj, tip)) return;
        /* ⚠ Fără utilizator conectat NU înregistrăm nimic. Altfel mesajul
           „Te-ai deconectat" (și orice altceva de pe ecranul de întâmpinare) ar
           repopula un jurnal tocmai golit, iar următorul om care se conectează pe
           același dispozitiv ar găsi urme de la cel dinainte. */
        if (typeof state === 'undefined' || !state || !state.currentUser) return;
        var text = String(mesaj);
        var t = String(tip || 'info');
        var ultim = _toastLog[0];
        if (ultim && ultim.text === text && ultim.type === t) {
            ultim.count = (ultim.count || 1) + 1;
            ultim.ts = Date.now();
        } else {
            _toastLog.unshift({ text: text, type: t, ts: Date.now(), count: 1 });
            if (_toastLog.length > TOASTLOG_MAX) _toastLog.length = TOASTLOG_MAX;
        }
        toastLogSave();
        /* Dacă jurnalul e deschis chiar acum, se actualizează sub ochii omului. */
        try {
            var p = document.getElementById('toastLogPanel');
            if (p && !p.classList.contains('hidden')) renderToastLogPanel();
        } catch (e) { /* ignorăm */ }
    } catch (e) { /* jurnalul nu are voie să strice niciodată afișarea mesajului */ }
}
window.toastLogAdd = toastLogAdd;

/* ── Împachetarea funcției de afișare ────────────────────────────────────────
   ⚠ NU rescriem `showNotification` — o împachetăm. Mesajul se afișează exact ca
   înainte; noi doar îl notăm. Dacă ceva din jurnal ar crăpa, afișarea rămâne
   neatinsă (apelul original e primul, în afara oricărui `try` al nostru).
   Garda `_toastLogWrapped` împiedică o a doua împachetare dacă fișierul ar fi
   încărcat de două ori. */
(function wrapShowNotification() {
    if (window._toastLogWrapped) return;
    if (typeof window.showNotification !== 'function') {
        /* `app-modules-2.js` nu s-a încărcat încă: reîncercăm la `load`. */
        window.addEventListener('load', function () { setTimeout(wrapShowNotification, 0); });
        return;
    }
    var original = window.showNotification;
    window.showNotification = function (message, type) {
        var r = original.apply(this, arguments);
        toastLogAdd(message, type || 'info');
        return r;
    };
    window._toastLogWrapped = true;
})();

/* ── Afișare ─────────────────────────────────────────────────────────────── */
function toastLogTimeAgo(ts) {
    try {
        var s = Math.max(0, Math.round((Date.now() - ts) / 1000));
        if (s < 60) return window.t('toastlog.justNow');
        var m = Math.round(s / 60);
        if (m < 60) return m + ' ' + window.t('toastlog.minShort');
        var h = Math.round(m / 60);
        return h + ' ' + window.t('toastlog.hourShort');
    } catch (e) { return ''; }
}

function renderToastLogPanel() {
    var panel = document.getElementById('toastLogPanel');
    if (!panel) return;
    var esc = window.escHtml || function (x) { return String(x == null ? '' : x); };
    var T = window.t || function (k) { return k; };

    var icons = {
        success: 'fa-check-circle',
        error: 'fa-exclamation-circle',
        warning: 'fa-exclamation-triangle',
        info: 'fa-info-circle'
    };
    var colors = {
        success: 'var(--success,#27ae60)',
        error: 'var(--danger,#e74c3c)',
        warning: 'var(--warning,#f5b301)',
        info: 'var(--text-secondary)'
    };

    var body = _toastLog.length
        ? _toastLog.map(function (e) {
            var ic = icons[e.type] || icons.info;
            var col = colors[e.type] || colors.info;
            var badge = (e.count > 1)
                ? '<span style="margin-left:6px; font-size:.72em; opacity:.75; border:1px solid var(--border,#333); border-radius:8px; padding:1px 6px;">×' + e.count + '</span>'
                : '';
            return '<div style="padding:10px 12px; border-bottom:1px solid var(--border,#333); display:flex; gap:9px; align-items:flex-start;">'
                 + '<i class="fas ' + ic + '" style="color:' + col + '; margin-top:3px; flex-shrink:0;"></i>'
                 + '<div style="min-width:0;">'
                 +   '<div style="line-height:1.45; word-break:break-word;">' + esc(e.text) + badge + '</div>'
                 +   '<div style="font-size:.72em; color:var(--text-secondary); margin-top:3px;">' + esc(toastLogTimeAgo(e.ts)) + '</div>'
                 + '</div></div>';
          }).join('')
        : '<p style="padding:16px; color:var(--text-secondary);">' + esc(T('toastlog.empty')) + '</p>';

    panel.innerHTML =
        '<div style="padding:10px 12px; font-weight:700; border-bottom:1px solid var(--border,#333); display:flex; justify-content:space-between; align-items:center; gap:8px;">'
      +   '<span style="display:flex; align-items:center; gap:8px; min-width:0; flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">'
      +     '<i class="fas fa-clock-rotate-left"></i> ' + esc(T('toastlog.title'))
      +   '</span>'
      +   '<span style="display:flex; align-items:center; gap:14px; flex-shrink:0;">'
      +     (_toastLog.length ? '<i class="fas fa-trash-can" data-tlact="clear" title="' + esc(T('toastlog.clear')) + '" aria-label="' + esc(T('toastlog.clear')) + '" style="cursor:pointer; color:var(--text-secondary); font-size:.95em;"></i>' : '')
      +     '<i class="fas fa-times" data-tlact="close" style="cursor:pointer;"></i>'
      +   '</span>'
      + '</div>'
      + '<p style="padding:8px 12px; margin:0; font-size:.75em; color:var(--text-secondary); border-bottom:1px solid var(--border,#333);">'
      +   esc(T('toastlog.hint')) + '</p>'
      + '<div style="max-height:60vh; overflow:auto;">' + body + '</div>';

    if (panel.dataset.tlBound !== '1') {
        panel.dataset.tlBound = '1';
        panel.addEventListener('click', function (ev) {
            var a = ev.target.closest('[data-tlact]');
            if (!a) return;
            var act = a.getAttribute('data-tlact');
            if (act === 'clear') return toastLogClear();
            if (act === 'close') { panel.classList.add('hidden'); return; }
        });
    }
}
window.renderToastLogPanel = renderToastLogPanel;

function openToastLog() {
    try {
        /* Închidem panoul de notificări: sunt două liste diferite, nu se suprapun. */
        var np = document.getElementById('notifPanel');
        if (np) np.classList.add('hidden');
    } catch (e) { /* ignorăm */ }
    var panel = document.getElementById('toastLogPanel');
    if (!panel) return;
    renderToastLogPanel();
    panel.classList.remove('hidden');
}
window.openToastLog = openToastLog;

/* Pornire: recuperăm jurnalul salvat în această filă. */
toastLogLoad();
